import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    CurrentCustomerUserQueryApi,
    DeliveryAddressFragmentApi,
    ManagerFragmentApi,
    PillCountsApi,
    useCurrentCustomerUserQueryApi,
} from 'graphql/generated';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useMemo } from 'react';
import { CurrentCustomerType, DeliveryAddressType, ManagerType, PillCountsType } from 'types/customer';

export function useCurrentCustomerData(fromCache = true): CurrentCustomerType | null | undefined {
    const [{ data, error }] = useCurrentCustomerUserQueryApi({
        requestPolicy: fromCache ? 'cache-first' : 'network-only',
    });
    const { useTaxId } = useDomainConfig();
    useQueryError(error);

    const currentCustomerUser = data?.currentCustomerUser;

    return useMemo(() => {
        if (currentCustomerUser === undefined) {
            return undefined;
        }

        return mapCurrentCustomerApiData(currentCustomerUser, useTaxId);
    }, [currentCustomerUser, useTaxId]);
}

const mapCurrentCustomerApiData = (
    apiCurrentCustomerData: CurrentCustomerUserQueryApi['currentCustomerUser'],
    useTaxId: boolean,
): CurrentCustomerType | null => {
    if (apiCurrentCustomerData === null) {
        return null;
    }

    return {
        ...apiCurrentCustomerData,
        companyCustomer: apiCurrentCustomerData.__typename === 'CompanyCustomerUser',
        telephone: apiCurrentCustomerData.telephone === null ? '' : apiCurrentCustomerData.telephone,
        birthDay: apiCurrentCustomerData.birthDay === null ? null : new Date(apiCurrentCustomerData.birthDay),
        companyName:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' && apiCurrentCustomerData.companyName !== null
                ? apiCurrentCustomerData.companyName
                : '',
        companyNumber:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' && apiCurrentCustomerData.companyNumber !== null
                ? apiCurrentCustomerData.companyNumber
                : '',
        companyTaxNumber:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' &&
            apiCurrentCustomerData.companyTaxNumber !== null
                ? apiCurrentCustomerData.companyTaxNumber
                : '',
        companyTaxIdNumber:
            useTaxId &&
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' &&
            apiCurrentCustomerData.companyTaxIdNumber !== null
                ? apiCurrentCustomerData.companyTaxIdNumber
                : '',
        companyName2:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' && apiCurrentCustomerData.companyName2 !== null
                ? apiCurrentCustomerData.companyName2
                : '',
        companyEmail:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' && apiCurrentCustomerData.companyEmail !== null
                ? apiCurrentCustomerData.companyEmail
                : '',
        companyTelephone:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' &&
            apiCurrentCustomerData.companyTelephone !== null
                ? apiCurrentCustomerData.companyTelephone
                : '',
        sapId:
            apiCurrentCustomerData.__typename === 'CompanyCustomerUser' && apiCurrentCustomerData.sapId !== null
                ? apiCurrentCustomerData.sapId
                : '',
        deliveryAddresses: mapDeliveryAddresses(apiCurrentCustomerData.deliveryAddresses),
        defaultDeliveryAddress: apiCurrentCustomerData.defaultDeliveryAddress
            ? mapDeliveryAddress(apiCurrentCustomerData.defaultDeliveryAddress)
            : null,
        passwordOld: '',
        passwordFirst: '',
        passwordSecond: '',
        pillCounts: mapPillCounts(apiCurrentCustomerData.pillCounts),
        customerCards: apiCurrentCustomerData.customerCards,
        codDisabled: apiCurrentCustomerData.codDisabled,
        loginInfo: apiCurrentCustomerData.loginInfo,
        quickContact: apiCurrentCustomerData.quickContact,
        b2bPermissions: apiCurrentCustomerData.b2bPermissions,
    };
};

export const mapDeliveryAddress = (apiDeliveryAddressData: DeliveryAddressFragmentApi): DeliveryAddressType => {
    return {
        ...apiDeliveryAddressData,
        companyName: apiDeliveryAddressData.companyName ?? '',
        companyName2: apiDeliveryAddressData.companyName2 ?? '',
        street: apiDeliveryAddressData.street ?? '',
        houseNumber: apiDeliveryAddressData.houseNumber ?? '',
        district: apiDeliveryAddressData.district ?? null,
        city: apiDeliveryAddressData.city ?? '',
        postcode: apiDeliveryAddressData.postcode ?? '',
        telephone: apiDeliveryAddressData.telephone ?? '',
        email: apiDeliveryAddressData.email ?? '',
        firstName: apiDeliveryAddressData.firstName ?? '',
        lastName: apiDeliveryAddressData.lastName ?? '',
        country: apiDeliveryAddressData.country?.name ?? '',
        sapId: apiDeliveryAddressData.sapId ?? '',
        manager: apiDeliveryAddressData.manager ? mapManager(apiDeliveryAddressData.manager) : null,
    };
};

export const mapManager = (apiManagerData: ManagerFragmentApi): ManagerType => {
    return {
        telephone: apiManagerData.telephone ?? null,
        email: apiManagerData.email ?? null,
        sapId: apiManagerData.sapId,
        fullName: apiManagerData.fullName,
    };
};

export const mapDeliveryAddresses = (apiDeliveryAddressesData: DeliveryAddressFragmentApi[]): DeliveryAddressType[] => {
    return apiDeliveryAddressesData.map((address) => mapDeliveryAddress(address));
};

const mapPillCounts = (pillCountsData: PillCountsApi): PillCountsType => {
    return {
        watchDogItems: pillCountsData.watchDogItems,
        claimsAndReturnsItems: pillCountsData.claimsAndReturnsItems,
    };
};
